import { usePermission } from '@/app/usePermission';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { setForbiddenHandler, setUnauthorizedHandler } from '@/services/api';
import { ADMIN_PASSWORD_PATH, isPasswordChangeRefusal } from '@/services/adminSession';
import { clearReauthStamp, decideOn401, redirectToLogin } from '@/services/reauth';
import { useTranslation } from '@/i18n/useTranslation';
import { sessionExpired as dict } from '@/i18n/dictionaries/sessionExpired';
import { common } from '@/i18n/dictionaries/common';
import { sessionApi } from '@/app/api/sessionApi';
import type { RequirePermissionProps } from '@/app/types/RequirePermissionProps';
import type { RequireSessionProps } from '@/app/types/RequireSessionProps';
import type { SessionExpiredPanelProps } from '@/app/types/SessionExpiredPanelProps';
import type { SessionProviderProps } from '@/app/types/SessionProviderProps';
import type { SessionState } from '@/app/types/SessionState';
import type { SessionUser } from '@/app/types/SessionUser';
import '@/app/session-expired.css';

// MPass session bootstrap: the SPA never handles credentials — it only checks whether
// an MPass-established backend session exists and redirects to SAML login otherwise.
const SessionContext = createContext<SessionState>({
  user: null,
  loading: true,
  refresh: async () => {},
});

export function SessionProvider({ children }: SessionProviderProps) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  // A dead session answers 401 to EVERY request that follows, not one per user action. The
  // first one has to decide what happens; the rest must not decide again.
  //
  // Without this, the panel below defeats itself. Five mutation handlers reload their list
  // after the catch — NewsAdminPage's transition and delete, PagesAdminPage's two, and
  // WorkQueuePage's — so the reload runs even when the mutation failed. Its own 401 arrives
  // moments later, is read as an ordinary GET, and navigates away from the panel the mutation
  // just raised, taking the unsaved work the panel exists to protect. Fixing it here rather
  // than at those five call sites is deliberate: the same trap catches the sixth one somebody
  // writes next month, and the reload after a 409 conflict — which genuinely does need to run,
  // to show what the other user did — keeps working untouched.
  //
  // A ref, not state: the handler closure is built once per `user` and would capture a stale
  // `expired` forever. Cleared only when a session is genuinely back (see refresh), never on
  // dismiss — the user who pressed "stay on the page" is copying their text out, and yanking
  // them to MPass mid-copy is the exact thing they just declined.
  const expiryAnnounced = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setUser(await sessionApi.me());
      // The round trip worked, so a later expiry deserves a fresh first attempt rather than
      // being read as the second half of a bounce. See services/reauth.ts.
      clearReauthStamp();
      expiryAnnounced.current = false;
      setExpired(false);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Mid-session expiry. Installed only once there IS a user, which is what keeps the mount-time
  // bootstrap out of this path: /api/v1/me's 401 on a cold, signed-out visit must reject
  // quietly so RequireSession — not api.ts — decides what a signed-out visitor sees. The gating
  // is load-bearing, not incidental: without it the handler would fire before React's first
  // render and pre-empt that decision (including on the unguarded 404 route).
  useEffect(() => {
    if (!user) return;
    setUnauthorizedHandler(({ method }) => {
      // The policy itself lives in services/reauth.ts as a pure function, where it is tested.
      // The ApiError still propagates to the page's own catch either way, so existing error
      // handling is unchanged by any of these branches.
      switch (decideOn401(method, expiryAnnounced.current)) {
        case 'redirect':
          redirectToLogin();
          return;
        case 'announce':
          expiryAnnounced.current = true;
          setExpired(true);
          return;
        case 'ignore':
          return;
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [user]);

  // The administration's password session (ADM-1): until a temporary password is changed, every
  // administration route answers 403, and the tab is sent to change it rather than left on a page
  // of refusals. The refusal's code says which 403 that is (services/adminSession.ts).
  useEffect(() => {
    if (!user) return;
    setForbiddenHandler(({ problem }) => {
      if (isPasswordChangeRefusal(problem)) {
        window.location.assign(ADMIN_PASSWORD_PATH);
      }
    });
    return () => setForbiddenHandler(null);
  }, [user]);

  return (
    <SessionContext.Provider value={{ user, loading, refresh }}>
      {children}
      {expired && <SessionExpiredPanel onDismiss={() => setExpired(false)} />}
    </SessionContext.Provider>
  );
}

/**
 * Shown when a save fails with 401. Deliberately not a redirect: the one thing an automatic
 * navigation buys over a single click is that click, and it costs an entire unsaved article.
 * "Rămâneți pe pagină" exists so the user can copy their text out first — the session is gone
 * either way, so dismissing buys time, not a working session.
 */
function SessionExpiredPanel({ onDismiss }: SessionExpiredPanelProps) {
  const { t } = useTranslation(dict);
  return (
    <>
      <div className="session-expired__overlay" />
      <div
        className="session-expired"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
      >
        <h2 className="session-expired__title" id="session-expired-title">
          {t('title')}
        </h2>
        <p className="session-expired__body">{t('body')}</p>
        <div className="session-expired__actions">
          <button type="button" className="btn btn--ghost" onClick={onDismiss}>
            {t('dismiss')}
          </button>
          <button type="button" className="btn btn--primary" onClick={() => redirectToLogin()}>
            {t('reauthenticate')}
          </button>
        </div>
      </div>
    </>
  );
}

export function useSession() {
  return useContext(SessionContext);
}

export function RequireSession({ children }: RequireSessionProps) {
  const { user, loading } = useSession();
  const { t } = useTranslation(common);
  if (loading) return <div className="empty-state">{t('loading')}</div>;
  if (!user) {
    // Routed through redirectToLogin so this pre-existing path gets the loop guard too: this
    // assignment runs from the render body on EVERY render where user is null, so if the
    // session cookie never survives the login round trip (the known SameSite/ACS blocker) it
    // spins today, unguarded. Now the second attempt inside 10s lands on the refusal screen.
    redirectToLogin();
    return null;
  }
  return <>{children}</>;
}

// Route-level guard for screens gated on a PermissionCatalog code, mirroring the
// backend's @PreAuthorize on the same endpoint — so a direct URL hit renders a clear
// message instead of an empty table backed by silent 403s (frontend RBAC gap G2).
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  return (
    <RequireSession>
      <PermissionGate permission={permission}>{children}</PermissionGate>
    </RequireSession>
  );
}

function PermissionGate({ permission, children }: RequirePermissionProps) {
  const allowed = usePermission(permission);
  const { t } = useTranslation(common);
  if (!allowed) return <div className="empty-state">{t('accessDenied')}</div>;
  return <>{children}</>;
}
